import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon } from "@msys/ui";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useGlobalLoadingCounter } from "../../global-loading-state/useGlobalLoadingCounter.js";
import { useSapS4HanaGetSalesQuotationFromRemoteLazyQuery } from "./S4HanaGetSalesQuotationFromRemoteMenuButton.generated.js";
import { assert } from "@msys/common";

interface Props {
  salesQuotationId: string | undefined;
  title: string;
  disabled?: boolean;
}

export const S4HanaGetSalesQuotationFromRemoteMenuButton = React.forwardRef<
  HTMLLIElement,
  Props
>((props, ref) => {
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  const { enqueueSnackbar } = useSnackbar();
  const getSalesQuotationFromRemote = useGetSalesQuotationFromRemote();

  const { increment, decrement } = useGlobalLoadingCounter();

  return (
    <MenuItemWithIcon
      ref={ref}
      icon={<BugReportIcon />}
      onClick={async () => {
        try {
          increment();
          setStatus("loading");
          assert(props.salesQuotationId, "salesQuotationId is undefined");
          await getSalesQuotationFromRemote(props.salesQuotationId);
          setStatus("idle");
        } catch (e) {
          if (e instanceof Error)
            enqueueSnackbar(e.message, { variant: "error" });
          Sentry.captureException(e);
          setStatus("error");
        } finally {
          decrement();
        }
      }}
      disabled={
        props.disabled || !props.salesQuotationId || status === "loading"
      }
    >
      {props.title}
    </MenuItemWithIcon>
  );
});

const useGetSalesQuotationFromRemote = () => {
  const client = useApolloClient();
  const [sapS4HanaGetSalesQuotationFromRemote] =
    useSapS4HanaGetSalesQuotationFromRemoteLazyQuery({ client });

  return useCallback(
    async (salesQuotationId: string) => {
      const result = await sapS4HanaGetSalesQuotationFromRemote({
        variables: {
          salesQuotationId,
        },
      });

      if (result.data) {
        download(
          result.data.sapS4HanaGetSalesQuotationFromRemote.salesQuotation,
          `sales-quotation-from-remote-${salesQuotationId}.json`
        );
      }
    },
    [sapS4HanaGetSalesQuotationFromRemote]
  );
};

function download(obj: any, filename: string) {
  const jsonString = JSON.stringify(obj, null, 2); // Pretty formatted
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const elem = document.createElement("a");
  elem.href = url;
  elem.download = filename;
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);

  URL.revokeObjectURL(url);
}
